package com.eladfinish.bible.contest.screens.chapterComplete.usecases

import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.constants.ColorConstants.INDIGO_DARK
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersState.PartUiState
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.model.ContentMode
import com.eladfinish.bible.contest.text.ClickAction
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.text.SpannedRange
import kotlinx.collections.immutable.ImmutableMap
import kotlinx.collections.immutable.toImmutableList
import kotlinx.collections.immutable.toImmutableMap

class RevealBlankUseCase {

    operator fun invoke(
        parts: ImmutableMap<Int, PartUiState>,
        currentPart: Int,
        originWord: String,
        endsWithMakaf: Boolean,
        revealedSizeProportion: Float = 1.08f,
    ): ImmutableMap<Int, PartUiState> {
        val ready = parts[currentPart] as? PartUiState.Ready
            ?: return parts

        val mode = ready.contentMode as? ContentMode.ChapterComplete
            ?: return parts

        val model = mode.output

        val clickableRangeIndex = model.spannedRangeList.indexOfFirst { range ->
            val action = (range.spanType as? SpanType.Clickable)?.action
            action is ClickAction.RevealBlank && action.originWord == originWord
        }
        if (clickableRangeIndex == -1) return parts

        val clickableRange = model.spannedRangeList[clickableRangeIndex]
        val start = clickableRange.textRange.start
        val end = clickableRange.textRange.end

        val raw = model.charSequence.toString()
        if (start < 0 || end > raw.length || start >= end) return parts

        // In the builder, when a word ends with a makaf, the blank sometimes keeps an extra space.
        // We remove that extra char from the output when revealing (shift = 1).
        val shift = if (endsWithMakaf) 1 else 0

        val newRaw = buildString {
            append(raw.take(start))
            append(originWord)
            val afterStart = (end + shift).coerceAtMost(raw.length)
            append(raw.substring(afterStart))
        }

        val replacedLen = end - start
        val delta = originWord.length - replacedLen
        val revealedEnd = start + originWord.length

        val newRanges = model.spannedRangeList
            .asSequence()
            .mapNotNull { range ->
                // Remove the clickable span after revealing.
                if (range == clickableRange && range.spanType is SpanType.Clickable) {
                    return@mapNotNull null
                }

                val rStart = range.textRange.start
                val rEnd = range.textRange.end

                // Before replaced range -> unchanged.
                if (rEnd <= start) return@mapNotNull range

                // After replaced range -> shift.
                if (rStart >= end) {
                    return@mapNotNull range.copy(
                        textRange = TextRange(
                            start = rStart + delta - shift,
                            end = rEnd + delta - shift,
                        ),
                    )
                }

                // Overlaps replaced range -> drop.
                // This intentionally removes the blank styling (e.g., serif typeface) so the revealed
                // word can use the default theme font.
                null
            }
            .toMutableList()

        // Styling for the revealed word:
        // - Color: INDIGO_DARK (same as the blank color you used)
        // - Slightly bigger than base text
        // - No CustomTypeface span -> keeps theme default font
        newRanges += SpannedRange(
            spanType = SpanType.ForegroundColor(
                color = INDIGO_DARK,
            ),
            textRange = TextRange(
                start = start,
                end = revealedEnd,
            ),
        )
        newRanges += SpannedRange(
            spanType = SpanType.RelativeSize(
                proportion = revealedSizeProportion,
            ),
            textRange = TextRange(
                start = start,
                end = revealedEnd,
            ),
        )

        val updated = SpannableStringKMP(
            charSequence = newRaw,
            spannedRangeList = newRanges.toImmutableList(),
        )

        val updatedMode = mode.copy(
            output = updated,
        )

        return parts
            .plus(
                pair = currentPart to ready.copy(
                    contentMode = updatedMode,
                ),
            )
            .toImmutableMap()
    }
}
